import { useState } from 'react';
import { useDiffitUserContext } from '../../../contexts/DiffitUserContext';
import { markUserNuxAsSeen } from '../../../services/UserService';

/**
 * Per-user dismissal for the cards pinned to the bottom of AppSidebar (the BTS
 * tip card and SidebarMessage's welcome/classic cards).
 *
 * Each card owns a flat, versioned top-level `seen_nux_json` key: the server
 * merges seen_nux at the top level only, so nesting every card under one object
 * would let a partial POST from elsewhere clobber its siblings. Versioning the
 * key also means a future card in this slot shows up again for teachers who
 * dismissed the previous one.
 *
 * Signed-out users have no profile to persist against, so they fall back to a
 * session-only dismiss that resets on refresh.
 */
export function useDismissibleSidebarCard(nuxKey) {
  const { diffitUser, setDiffitUser } = useDiffitUserContext();
  const [sessionDismissed, setSessionDismissed] = useState(false);

  const isDismissed = diffitUser
    ? (diffitUser.seen_nux_json?.[nuxKey]?.dismissed ?? false)
    : sessionDismissed;

  const dismiss = () => {
    if (!diffitUser) {
      setSessionDismissed(true);
      return;
    }

    setDiffitUser((prev) => ({
      ...prev,
      seen_nux_json: { ...prev.seen_nux_json, [nuxKey]: { dismissed: true } },
    }));

    // The no-op custom error handler keeps a failed persist out of the global
    // error modal: markUserNuxAsSeen's defaultErrorMessage is sign-in copy
    // ("There was an error logging you in."), which is nonsense for a dismissal
    // every signed-in user can trigger. Same suppression MathPromoBanner.jsx
    // uses for its post-claim nux write.
    markUserNuxAsSeen(
      diffitUser.id,
      { ...diffitUser.seen_nux_json, [nuxKey]: { dismissed: true } },
      () => {}
    ).catch(() => {
      // The local update above already hid the card; a failed persist just means
      // it can reappear next session. Swallow it so a dismissal never surfaces
      // as an unhandled rejection.
    });
  };

  // Persists the dismissal without hiding the card locally, so a card meant to be seen once
  // stays up for the rest of this visit and is gone on the next load.
  const markSeen = () => {
    if (!diffitUser) return;
    markUserNuxAsSeen(
      diffitUser.id,
      { ...diffitUser.seen_nux_json, [nuxKey]: { dismissed: true } },
      () => {}
    ).catch(() => {});
  };

  return { isDismissed, dismiss, markSeen };
}
